import { zodResolver } from '@hookform/resolvers/zod';
import { DialogTitle, DialogContent, Divider } from '@mui/material';
import { NewPlanSchema } from '@weft/types';
import { withDefault } from '@weft/utils';
import { DefaultThemeDialog } from 'apps/app/src/components/design/DefaultThemeDialog';
import { LocalStorageKeys } from 'apps/app/src/constants/local-storage-keys';
import { useContract } from 'apps/app/src/hooks/requests/use-contract';
import { useUpdateContract } from 'apps/app/src/hooks/requests/use-update-contract';
import { useValidatedParams } from 'apps/app/src/hooks/use-validated-params';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import { useForm, useFormContext } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useVyNavigate } from '../../../../hooks/use-vy-navigate';
import type { NewPlanForm } from '../../../plans/CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../../../plans/CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/plan-input-form';
import { ContractReviseDialogActions } from '../../contract-page-components/ContractPageDialogActions';
import type { UpdateContractForm } from '../ContractUpdateForm';
import { UpdateContractFormSchema } from '../ContractUpdateForm';
import { SaveContractDatePicker } from './SaveContractDatePicker';

type SaveContractProps = {
  open: boolean;
  onClose?: () => void;
};

export const SaveContractDialogForm: React.FC<SaveContractProps> = ({
  open,
  onClose,
}) => {
  const { contractId } = useValidatedParams('contractId');

  const {
    data: { contract },
  } = useContract(contractId, {
    refetchOnWindowFocus: false,
  });

  const queryClient = useQueryClient();
  const navigate = useVyNavigate();

  const { watch } = useFormContext<PlanInputForm, NewPlanForm>();

  const form = useForm<UpdateContractForm>({
    resolver: zodResolver(UpdateContractFormSchema),
    defaultValues: {
      startDate: contract.startDate,
    },
  });

  const updateContractMutation = useUpdateContract();

  const planInput = watch();
  const fixedProducts = withDefault(planInput.fixedProducts, []);
  const usageProducts = withDefault(planInput.usageProducts, []);
  const products = [...fixedProducts, ...usageProducts];

  const handleSubmit = form.handleSubmit(async ({ startDate }) => {
    const parsedNewPlan = NewPlanSchema.parse({
      ...planInput,
      products,
    });

    await runMutation(
      updateContractMutation,
      {
        contractId,
        updateContractRequest: {
          plan: parsedNewPlan,
          startDate,
        },
      },
      {
        pending: 'Updating...',
        success: 'Updated',
        error: 'Failed to update contract',
      },
      {
        onSuccess: () => {
          handleClose();
          localStorage.removeItem(LocalStorageKeys.UpdateContract);
          navigate(`/plans/contracts/${contractId}`);
  
          return queryClient.invalidateQueries({
            predicate: (query) => query.queryKey.includes(contractId),
          });
        },
      });
  });
   
  const handleClose = () => {
    onClose?.();
    form.reset();
  };

  const renderSaveContractDialogForm = () => {
    return (
      <>
        <DefaultThemeDialog
          open={open}
          onClose={handleClose}
          fullWidth
          PaperProps={{
            className: 'max-w-[780px]',
          }}
        >
          <DialogTitle>Contract Start Date</DialogTitle>
          <DialogContent>
            <Divider className="my-6" />
            <SaveContractDatePicker
              control={form.control}
              name="startDate"
            />
            <Divider className="my-6" />
          </DialogContent>
        
          <ContractReviseDialogActions
            handleClose={handleClose}
            handleSubmit={handleSubmit}
            text="Confirm & Update Contract"
          />
          
        </DefaultThemeDialog>

      </>
    );
  };

  return renderSaveContractDialogForm();
};